import React from 'react'
import { router, usePage, useForm } from '@inertiajs/react'
import {
  Box, Button, TextField, Select, MenuItem, FormControl, InputLabel,
  FormControlLabel, Switch, Paper, Typography, Grid,
} from '@mui/material'
import AppShell from '../../../components/layout/AppShell'
import PageHeader from '../../../components/ui/PageHeader'

Form.layout = (page) => <AppShell>{page}</AppShell>

export default function Form({ contact, action, errors: serverErrors = {} }) {
  const { props } = usePage()
  const tenant = props.tenant || {}
  const isEdit = action === 'edit'
  const basePath = `/m/${tenant.slug}/contacts`

  const { data, setData, post, put, processing, errors } = useForm({
    contact_type: contact.contact_type || 'Person',
    salutation: contact.salutation || '',
    title: contact.title || '',
    name1: contact.name1 || '',
    name2: contact.name2 || '',
    email: contact.email || '',
    phone: contact.phone || '',
    phone_business: contact.phone_business || '',
    phone_mobile: contact.phone_mobile || '',
    fax: contact.fax || '',
    website: contact.website || '',
    letter_salutation: contact.letter_salutation || '',
    alternative_recipient: contact.alternative_recipient || '',
    active: contact.active !== false,
    tax_number: contact.tax_number || '',
    vat_id: contact.vat_id || '',
    trade_register_number: contact.trade_register_number || '',
    legal_form: contact.legal_form || '',
    address: {
      street: contact.address?.street || '',
      house_number: contact.address?.house_number || '',
      postal_code: contact.address?.postal_code || '',
      city: contact.address?.city || '',
      country: contact.address?.country || 'DE',
    },
  })

  const allErrors = { ...serverErrors, ...errors }
  const isPerson = data.contact_type === 'Person'

  const handleSubmit = (e) => {
    e.preventDefault()
    if (isEdit) {
      put(`${basePath}/${contact.id}`)
    } else {
      post(basePath)
    }
  }

  const breadcrumbs = isEdit
    ? [{ label: 'Kontakte', href: basePath }, { label: contact.name1 || 'Bearbeiten', href: `${basePath}/${contact.id}` }, { label: 'Bearbeiten' }]
    : [{ label: 'Kontakte', href: basePath }, { label: 'Neuer Kontakt' }]

  return (
    <Box>
      <PageHeader
        title={isEdit ? 'Kontakt bearbeiten' : 'Neuer Kontakt'}
        breadcrumbs={breadcrumbs}
      />

      <Box component="form" onSubmit={handleSubmit} sx={{ maxWidth: 600 }} data-testid="contact-form">
        {/* Typ */}
        <Paper elevation={0} sx={{ p: 3, mb: 2, border: '1px solid', borderColor: 'divider' }}>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>Kontakttyp</Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Typ</InputLabel>
                <Select
                  value={data.contact_type}
                  label="Typ"
                  onChange={(e) => setData('contact_type', e.target.value)}
                  disabled={isEdit}
                  data-testid="contact-type-select"
                >
                  <MenuItem value="Person">Person</MenuItem>
                  <MenuItem value="Company">Unternehmen</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Paper>

        {/* Stammdaten */}
        <Paper elevation={0} sx={{ p: 3, mb: 2, border: '1px solid', borderColor: 'divider' }}>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>Stammdaten</Typography>
          <Grid container spacing={2}>
            {isPerson && (
              <>
                <Grid size={{ xs: 12 }}>
                  <FormControl fullWidth size="small" required error={!!allErrors.salutation}>
                    <InputLabel>Anrede</InputLabel>
                    <Select
                      value={data.salutation}
                      label="Anrede"
                      onChange={(e) => setData('salutation', e.target.value)}
                      data-testid="salutation-select"
                    >
                      <MenuItem value="herr">Herr</MenuItem>
                      <MenuItem value="frau">Frau</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Titel</InputLabel>
                    <Select
                      value={data.title}
                      label="Titel"
                      onChange={(e) => setData('title', e.target.value)}
                      data-testid="title-select"
                    >
                      <MenuItem value="">Kein Titel</MenuItem>
                      <MenuItem value="dr">Dr.</MenuItem>
                      <MenuItem value="prof">Prof.</MenuItem>
                      <MenuItem value="prof_dr">Prof. Dr.</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    label="Vorname"
                    required
                    fullWidth
                    size="small"
                    value={data.name2}
                    onChange={(e) => setData('name2', e.target.value)}
                    error={!!allErrors.name2}
                    helperText={allErrors.name2?.[0]}
                    slotProps={{ htmlInput: { 'data-testid': 'name2-input' } }}
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    label="Nachname"
                    required
                    fullWidth
                    size="small"
                    value={data.name1}
                    onChange={(e) => setData('name1', e.target.value)}
                    error={!!allErrors.name1}
                    helperText={allErrors.name1?.[0]}
                    slotProps={{ htmlInput: { 'data-testid': 'name1-input' } }}
                  />
                </Grid>
              </>
            )}

            {!isPerson && (
              <>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    label="Firmenname"
                    required
                    fullWidth
                    size="small"
                    value={data.name1}
                    onChange={(e) => setData('name1', e.target.value)}
                    error={!!allErrors.name1}
                    helperText={allErrors.name1?.[0]}
                    slotProps={{ htmlInput: { 'data-testid': 'name1-input' } }}
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    label="Abteilung"
                    fullWidth
                    size="small"
                    value={data.name2}
                    onChange={(e) => setData('name2', e.target.value)}
                    slotProps={{ htmlInput: { 'data-testid': 'name2-input' } }}
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    label="Rechtsform"
                    fullWidth
                    size="small"
                    value={data.legal_form}
                    onChange={(e) => setData('legal_form', e.target.value)}
                    slotProps={{ htmlInput: { 'data-testid': 'legal-form-input' } }}
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    label="Steuernummer"
                    fullWidth
                    size="small"
                    value={data.tax_number}
                    onChange={(e) => setData('tax_number', e.target.value)}
                    slotProps={{ htmlInput: { 'data-testid': 'tax-number-input' } }}
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    label="USt-IdNr."
                    fullWidth
                    size="small"
                    value={data.vat_id}
                    onChange={(e) => setData('vat_id', e.target.value)}
                    slotProps={{ htmlInput: { 'data-testid': 'vat-id-input' } }}
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    label="Handelsregisternummer"
                    fullWidth
                    size="small"
                    value={data.trade_register_number}
                    onChange={(e) => setData('trade_register_number', e.target.value)}
                    slotProps={{ htmlInput: { 'data-testid': 'trade-register-input' } }}
                  />
                </Grid>
              </>
            )}
          </Grid>
        </Paper>

        {/* Kontaktdaten */}
        <Paper elevation={0} sx={{ p: 3, mb: 2, border: '1px solid', borderColor: 'divider' }}>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>Kontaktdaten</Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <TextField
                label="E-Mail"
                type="email"
                fullWidth
                size="small"
                value={data.email}
                onChange={(e) => setData('email', e.target.value)}
                slotProps={{ htmlInput: { 'data-testid': 'email-input' } }}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                label="Telefon"
                fullWidth
                size="small"
                value={data.phone}
                onChange={(e) => setData('phone', e.target.value)}
                slotProps={{ htmlInput: { 'data-testid': 'phone-input' } }}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                label="Telefon (geschäftlich)"
                fullWidth
                size="small"
                value={data.phone_business}
                onChange={(e) => setData('phone_business', e.target.value)}
                slotProps={{ htmlInput: { 'data-testid': 'phone-business-input' } }}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                label="Mobiltelefon"
                fullWidth
                size="small"
                value={data.phone_mobile}
                onChange={(e) => setData('phone_mobile', e.target.value)}
                slotProps={{ htmlInput: { 'data-testid': 'phone-mobile-input' } }}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                label="Fax"
                fullWidth
                size="small"
                value={data.fax}
                onChange={(e) => setData('fax', e.target.value)}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                label="Webseite"
                fullWidth
                size="small"
                value={data.website}
                onChange={(e) => setData('website', e.target.value)}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                label="Briefanrede"
                fullWidth
                size="small"
                value={data.letter_salutation}
                onChange={(e) => setData('letter_salutation', e.target.value)}
                helperText="z.B. Sehr geehrter Herr Dr. Müller"
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                label="Alternativempfänger"
                fullWidth
                size="small"
                value={data.alternative_recipient}
                onChange={(e) => setData('alternative_recipient', e.target.value)}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={data.active}
                    onChange={(e) => setData('active', e.target.checked)}
                    data-testid="active-toggle"
                  />
                }
                label="Aktiv"
              />
            </Grid>
          </Grid>
        </Paper>

        {/* Adresse */}
        <Paper elevation={0} sx={{ p: 3, mb: 2, border: '1px solid', borderColor: 'divider' }}>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>Adresse</Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <TextField
                label="Straße"
                fullWidth
                size="small"
                value={data.address.street}
                onChange={(e) => setData('address', { ...data.address, street: e.target.value })}
                slotProps={{ htmlInput: { 'data-testid': 'street-input' } }}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                label="Hausnummer"
                fullWidth
                size="small"
                value={data.address.house_number}
                onChange={(e) => setData('address', { ...data.address, house_number: e.target.value })}
                slotProps={{ htmlInput: { 'data-testid': 'house-number-input' } }}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                label="PLZ"
                fullWidth
                size="small"
                value={data.address.postal_code}
                onChange={(e) => setData('address', { ...data.address, postal_code: e.target.value })}
                slotProps={{ htmlInput: { 'data-testid': 'postal-code-input' } }}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                label="Ort"
                fullWidth
                size="small"
                value={data.address.city}
                onChange={(e) => setData('address', { ...data.address, city: e.target.value })}
                slotProps={{ htmlInput: { 'data-testid': 'city-input' } }}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                label="Land"
                fullWidth
                size="small"
                value={data.address.country}
                onChange={(e) => setData('address', { ...data.address, country: e.target.value })}
                slotProps={{ htmlInput: { 'data-testid': 'country-input' } }}
              />
            </Grid>
          </Grid>
        </Paper>

        {/* Actions */}
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            type="submit"
            variant="contained"
            disabled={processing}
            data-testid="submit-btn"
          >
            {processing ? 'Wird gespeichert...' : 'Speichern'}
          </Button>
          <Button
            variant="outlined"
            onClick={() => router.visit(basePath)}
          >
            Abbrechen
          </Button>
        </Box>
      </Box>
    </Box>
  )
}
